import { Network } from 'lucide-react'
import { useMemo } from 'react'
import { ENTITY_ICON_COMPONENTS } from '@/components/neo4j/cytoscape/entity-icons'
import type { GraphComponent } from '@/services/api/graph'
import { ScoreChip } from '../primitives/ScoreChip'

export interface ClusterDetailPanelProps {
  component: GraphComponent
  label: string
  onOpenInGraph: () => void
}

function TypeChip({
  type,
  count,
  Icon,
}: {
  type: string
  count: number
  Icon?: React.ComponentType<{ className?: string }>
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md px-1.5 h-5 text-3xs font-medium bg-[var(--muted)]/60 text-[var(--muted-foreground)]">
      {Icon && <Icon className="size-3" />}
      {type} · <span className="text-[var(--foreground)] tabular-nums">{count}</span>
    </span>
  )
}

export function ClusterDetailPanel({ component, label, onOpenInGraph }: ClusterDetailPanelProps) {
  const typeCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const s of component.sample_entities) {
      const t = s.type || 'Other'
      map.set(t, (map.get(t) ?? 0) + 1)
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [component])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[var(--foreground)] break-words">{label}</div>
          <div className="text-3xs text-[var(--muted-foreground)] uppercase tracking-wider">
            {component.size} entities · {typeCounts.length} type
            {typeCounts.length === 1 ? '' : 's'}
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenInGraph}
          className="h-7 px-2.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-xs font-medium inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] shrink-0 max-md:h-9 max-md:px-3"
        >
          <Network className="size-3" /> Open in Graph
        </button>
      </div>
      {typeCounts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {typeCounts.map(([type, count]) => {
            const Icon = ENTITY_ICON_COMPONENTS[type]
            return <TypeChip key={type} type={type} count={count} Icon={Icon} />
          })}
        </div>
      )}
      {component.sample_entities.length > 0 && (
        <div className="rounded-md border border-[var(--border)]/70 divide-y divide-[var(--border)]/40 max-h-80 overflow-y-auto">
          {component.sample_entities.map(s => {
            const Icon = s.type ? ENTITY_ICON_COMPONENTS[s.type] : null
            return (
              <div
                key={s.id}
                className="grid grid-cols-[1fr_90px_auto] items-center gap-2 px-2.5 py-1.5"
              >
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  {Icon && <Icon className="size-3 text-[var(--muted-foreground)] shrink-0" />}
                  <span className="text-xs text-[var(--foreground)] truncate">
                    {s.name ?? `${s.type || 'Entity'} ${s.id.slice(-6)}`}
                  </span>
                </span>
                <span className="text-3xs text-[var(--muted-foreground)] truncate">{s.type}</span>
                <ScoreChip value={s.score} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
